import * as React from 'react';
import { ColumnVisibility, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
	DropdownMenuCheckboxItem,
} from '@components/molecules';
import type { DataGridColumnDef, DataGridStrings } from '@components/organisms';
interface ColumnVisibilityMenuProps<T> {
	columns: DataGridColumnDef<T>[];
	hiddenColumns: Set<string>;
	onVisibilityChange: (columnKey: string, isVisible: boolean) => void;
	strings?: DataGridStrings;
}

// column types that should not be visible in the column visibility menu
const columnTypesToHide = ['actions', 'filter-only'];

export function ColumnVisibilityMenu<T>({
	columns,
	hiddenColumns,
	onVisibilityChange,
	strings,
}: ColumnVisibilityMenuProps<T>) {
  // Filter out columns that are only for filtering purposes, as these columns will never render on the UI
  	columns = columns.filter(column => column.type !== 'filter-only');
	const allVisible = columns.every(column => !hiddenColumns.has(column.key));

	const handleSelectAllChange = (checked: boolean) => {
		// Iterate over each column and update its visibility.
		columns.forEach(column => {
			onVisibilityChange(column.key, checked);
		});
	};

	return (
		<div data-aid="data-grid-column-visibility-menu">
			<DropdownMenu modal={false}>
				<TooltipProvider delayDuration={0} skipDelayDuration={0}>
					<Tooltip>
						<TooltipTrigger asChild>
							{
								<DropdownMenuTrigger
									className="flex items-center justify-between p-0 text-sm font-medium text-gray-700 focus:outline-hidden"
									aria-label={strings?.columnVisibility?.buttonLabel || 'Toggle column visibility'}
								>
									<ColumnVisibility className="text-primary" />
								</DropdownMenuTrigger>
							}
						</TooltipTrigger>
						<TooltipContent>{strings?.filters?.buttonLabel || 'Column visibility'}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
				<DropdownMenuContent scrollable>
					<DropdownMenuCheckboxItem
						key="select-all"
						checked={allVisible}
						onCheckedChange={(checked: boolean) => handleSelectAllChange(checked)}
						onSelect={e => e.preventDefault()}
					>
						{strings?.columnVisibility?.selectAll || 'Select All'}
					</DropdownMenuCheckboxItem>

					<hr className="border-t my-2" />

					{columns.map(column => {
						if (columnTypesToHide.includes(column.type)) return null;
						return (
							<DropdownMenuCheckboxItem
								checked={!hiddenColumns.has(column.key)}
								key={column.key}
								onCheckedChange={(checked: boolean) => onVisibilityChange(column.key, checked)}
								onSelect={e => {
									// Prevent the selection from closing the dropdown
									e.preventDefault();
								}}
							>
								{column.header || column.key}
							</DropdownMenuCheckboxItem>
						);
					})}
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
}
